// Create a new file: src/features/polotno/component/presentation/PresentationPanel.tsx
import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useGlobalContext } from '@src/context/globalContext';
import { CloseOutlined } from '@ant-design/icons'
import { Button, Modal, Input, Select, message } from 'antd';
import { getAudioDurationInSecondsCustom } from '@src/shared/utils/VideoGeneration/audioGeneration';
import { animationFuncCustomeOne } from '@src/shared/utils/VideoGeneration/helperFunctions/animations';
import { generateUniqueId } from '@src/shared/utils/core';
import { fetchReplicas } from '@src/shared/utils/VideoGeneration/helperFunctions/tavusApi';
import { useGetGettyImageVideos } from '@src/api/gettyImages/getGettyImageVideo';
import { generateSnapShot, checkVideoGeneratedStatus } from '@src/shared/utils/VideoGeneration/helperFunctions/webSnapShot';
import './presentationSettings.less';

export const PresentationPanel = observer(({store}: any) => {
  const {
    ScriptDetails: [scriptDetails, setScriptDetails],
    SelectedSectionIndex: [selectedSectionIndex],
    ShowPresentation: [showPresentation, setShowPresentation],
    UserDetail: [userDetail]
  } = useGlobalContext();
  useEffect(() => {
    setSelectedOrientationValue(
      userDetail?.purpose?.size === "9:16" ? "vertical" : "horizontal"
    );
  }, [userDetail]);
  const [tabPositionAvatar, setTabPositionAvatar] = useState('noAvatar');
  const [selectedOrientationValue, setSelectedOrientationValue] = useState(
    userDetail?.purpose?.size === "9:16" ? "vertical" : "horizontal"
  );
  const [searchQuery, setSearchQuery] = useState(
    ""
  );
  const [replicasTavus, setReplicasTavus] = useState<any[]>([]);
  
  useEffect(() => {
    // Fetch replicas once when component mounts
    const fetchReplicasData = async () => {
      try {
        const replicas = await fetchReplicas({
          page: 1,
          verbose: true,
          replica_ids: '',
        });
        setReplicasTavus(replicas);
      } catch (error) {
        console.error('Failed to fetch replicas:', error);
      }
    };
    
    fetchReplicasData();
  }, []);
  
  useEffect(() => {
    setSearchQuery(
      scriptDetails?.data[selectedSectionIndex]?.searchQueries?.[0] ||
      store?.activePage?.custom?.searchQuery ||
      ""
    );
  }, [scriptDetails, selectedSectionIndex, store]);
  const [
    transformedGettyImageVideos,
    setTransformedGettyImageVideos,
  ] = useState<any[]>([]);
  const {
    data: gettyImageVideos,
    isLoading: isGettyImageVideoLoading,
    isSuccess: isGettyImageVideosSuccess,
    page: gettyVideoPage,
    setPage: setGettyVideoPage,
  } = useGetGettyImageVideos(
    searchQuery,
    userDetail?.purpose?.size,
    selectedOrientationValue,
    15,
    1,
    userDetail?.purpose?.videotechniques || []
  );
  
  useEffect(() => {
    if (isGettyImageVideosSuccess && gettyImageVideos?.videos) {
      const newVideos = gettyImageVideos.videos.map((video: any) => {
        const videoPreview = video.display_sizes.find(
          (displaySize: any) => displaySize.name === "preview_1"
        );
        const imagePreview = video.display_sizes.find(
          (displaySize: any) => displaySize.name === "thumb"
        );

        return {
          id: video.id,
          image_url: imagePreview?.uri || "",
          video_url: videoPreview?.uri || "",
          type: "video",
        };
      });

      // Append new videos to existing state
      setTransformedGettyImageVideos((prevVideos) => [
        ...prevVideos,
        ...newVideos,
      ]);
    }
  }, [isGettyImageVideosSuccess, gettyImageVideos]);

  useEffect(() => {
    if(scriptDetails?.data[selectedSectionIndex]?.aiAvatarProps){
    if (scriptDetails?.data[selectedSectionIndex]?.aiAvatarProps) {
      if (scriptDetails?.data[selectedSectionIndex]?.aiAvatarProps?.noAvatar) {
        setTabPositionAvatar('noAvatar')
      } else if (
        scriptDetails?.data[selectedSectionIndex]?.aiAvatarProps?.avatarWithBg
      ) {
        setTabPositionAvatar('avatarWithBg')
      } else if (scriptDetails.data[selectedSectionIndex]?.aiAvatarProps?.avatar) {
        setTabPositionAvatar('avatar')
      } else if ( scriptDetails?.data[selectedSectionIndex]?.aiAvatarProps?.avatarWithWebsite){
        setTabPositionAvatar('avatarWithWebsite')
      }
    //   if (scriptDetails.data[selectedSectionIndex]?.aiAvatarProps?.scriptAvatar) {
    //     setTabPositionVoice('native')
    //   } else {
    //     setTabPositionVoice('custom')
    //   }
    } else {
      setTabPositionAvatar('noAvatar')

    //   setTabPositionVoice('custom')
    }
}
  }, [scriptDetails, selectedSectionIndex])

  const durationCalculation = async () => {
    const audioUrl = scriptDetails?.data?.[selectedSectionIndex]?.url
    let duration = 2 // Default duration in seconds

    // Calculate audio duration if the URL is available
    if (audioUrl) {
      duration = await getAudioDurationInSecondsCustom(audioUrl)
    }

    const newDuration = duration * 1000 // Convert duration to milliseconds
    const pages = store.toJSON().pages

    // Filter pages matching the given scriptIndex
    const totalPages = pages
      .filter((page: any) => page?.custom?.scriptIndex === selectedSectionIndex)
      .map((page: any) => page.id)

    const durationPerPage = newDuration / totalPages.length

    totalPages.forEach((pageId: string) => {
      store
        .getElementById(pageId)
        .set({ duration: durationPerPage, animations: animationFuncCustomeOne(durationPerPage) })
    })
  }
  
  const fullAvatarScene = async () => {
    setTabPositionAvatar('avatar')

    const backupAssets: any = []
    const pages = store.toJSON().pages
    const currentIndexPages = pages
      .filter((page: any) => page?.custom?.scriptIndex === selectedSectionIndex)
      .map((page: any) => ({
        id: page.id,
        position: pages.findIndex((p: any) => p.id === page.id),
      }))
      .sort((a: any, b: any) => a.position - b.position)
      .map((page: any) => page?.id)

    // Only delete pages if there are multiple pages
    if (currentIndexPages.length > 1) {
      // Get all page IDs except the last one
      const pageIdsToDelete = currentIndexPages.slice(0, -1)

      currentIndexPages.forEach((pageId: string, index: number) => {
        const page = store.getElementById(pageId)
        // Find the first video or image child and break
        for (const child of page.children) {
          if (child.type === 'video' || child.type === 'image') {
            backupAssets.push({searchQueries:[''], duration:0.9, mediaUrl:child?.src, type:child?.type})
            break; // Stop after finding the first video or image
          }
        }
      })
      // Delete all pages except the last one
      store.deletePages(pageIdsToDelete)
  
    }
    // Now work with the remaining page (last page)
    const lastPageId = currentIndexPages[currentIndexPages.length - 1]
    const selectedpage = store.getElementById(lastPageId)
    store.selectPage(lastPageId);
    selectedpage.children.map((child: any) => {
      if (child.type == 'video' || child.type == 'image') {
        store.deleteElements([child.id])
      }
    })

    // Use the state instead of making the API call
    const initialAvatarUrl = replicasTavus.find((replica: any) => {
      const avatarReplicaId = userDetail?.purpose?.avatarReplicaId
      const sizeCondition = userDetail?.purpose?.size === '9:16'

      // Determine the replica ID based on the conditions
      let replicaId = ''
      if (avatarReplicaId) {
        replicaId = avatarReplicaId
      } else {
        replicaId = sizeCondition ? 'ra5e60eeb07f' : 'r79e1c033f'
      }

      return replica.replica_id === replicaId
    })

    const elementId = generateUniqueId()
    selectedpage.addElement({
      src: initialAvatarUrl?.thumbnail_video_url || '',
      type: 'video',
      id: elementId,
      // duration: pageDuration,
      x: -25,
      y: -25,
      width: store.width + 50,
      height: store.height + 50,
      cornerRadius: 20,
      // mask:true,

      custom: {
        gettyId: elementId,
        mainElement: false,
        aiAvatar: true,
        scriptIndex: selectedSectionIndex,
      },
    })
    
    durationCalculation()

    setScriptDetails((prevScript: { data: any }) => {
      const updatedScript = { ...prevScript }

 

      const updatedData = [...prevScript.data]

      updatedData[selectedSectionIndex] = {
        ...updatedData[selectedSectionIndex],
        aiAvatarProps: {
          ...updatedData[selectedSectionIndex].aiAvatarProps,
          noAvatar: false,
          avatarWithBg: false,
          avatarWithWebsite: false,
          avatar: true,
        },
        images: [
          updatedData[selectedSectionIndex]?.images?.[0] ?? { searchQueries: [], duration: 0.9 }
        ],
        backupImages: backupAssets ?? [],
      }
      return { ...updatedScript, data: updatedData }
    })
  }

  const handleMediaBroll = (position:string) => {
    if(position == 'noAvatar'){
    if(scriptDetails?.data[selectedSectionIndex]?.backupImages?.length > 0){
      addPageSceneWithBackUpImage(scriptDetails?.data[selectedSectionIndex]?.backupImages)
      setScriptDetails((prevScript: { data: any }) => {
        const updatedScript = { ...prevScript }
        const updatedData = [...prevScript.data]
        updatedData[selectedSectionIndex] = {
          ...updatedData[selectedSectionIndex],
          aiAvatarProps: {
            ...updatedData[selectedSectionIndex].aiAvatarProps,
            noAvatar: true,
            avatarWithBg: false,
            avatar: false,
            avatarWithWebsite: false,
          },
          // images: [updatedData[scriptIndex]?.images?.[0] || {searchQueries: [], duration: 0.9}]
        }

        return { ...updatedScript, data: updatedData }
      })
    }else{
      if(transformedGettyImageVideos?.[0]){
        setTabPositionAvatar('noAvatar')
        addPageScene(transformedGettyImageVideos?.[0])
        setScriptDetails((prevScript: { data: any }) => {
            const updatedScript = { ...prevScript }
            const updatedData = [...prevScript.data]
            updatedData[selectedSectionIndex] = {
              ...updatedData[selectedSectionIndex],
              aiAvatarProps: {
                ...updatedData[selectedSectionIndex].aiAvatarProps,
                noAvatar: true,
                avatarWithBg: false,
                avatar: false,
                avatarWithWebsite: false,
              },
              // images: [updatedData[scriptIndex]?.images?.[0] || {searchQueries: [], duration: 0.9}]
            }
    
            return { ...updatedScript, data: updatedData }
          })
        }else{
            message.warning('No Media Found')
        }
    }
   
    }else if(position == 'avatarWithBg'){
    if(transformedGettyImageVideos?.[0]){
      setTabPositionAvatar('avatarWithBg')
      handleBackGroundAvatar(transformedGettyImageVideos?.[0])
      const item = transformedGettyImageVideos?.[0]
      setScriptDetails((prevScript: { data: any }) => {
        const updatedScript = { ...prevScript }

        const updatedData = [...prevScript.data]

        updatedData[selectedSectionIndex] = {
          ...updatedData[selectedSectionIndex],
          aiAvatarProps: {
            ...updatedData[selectedSectionIndex].aiAvatarProps,
            noAvatar: false,
            avatarWithBg: true,
            avatar: false,
            avatarWithWebsite: false,
            images: [updatedData[selectedSectionIndex]?.images?.[0] || {searchQueries: [], duration: 0.9, mediaUrl: item.video_url || item.image_url || '', type: item?.type || 'image'}],
            backupImages: updatedData[selectedSectionIndex]?.images ?? [],
          },
          // backupImages: [updatedData[selectedSectionIndex]?.images?.[0] || { searchQueries: [], duration: 0.9 }],
        }

        return { ...updatedScript, data: updatedData }
      })
    }else{
      message.warning('No Media Found')
    }
  }
}

  const addPageScene = (item: any) => {
    let pageId = ''
    store.pages.map((page: any) => {
      if(page?.custom?.scriptIndex === selectedSectionIndex){
        pageId = page.id
        return;
      }
    })

    // Get the original page
    const originalPage = store.getElementById(pageId);
    
    // Find and temporarily remove caption elements
    const captionElements: any[] = [];
    originalPage.children.forEach((child: any) => {
      if (child.id.includes('captionText')) {
        captionElements.push({
          ...child.toJSON(),
          id: child.id
        });
        store.deleteElements([child.id]);
      }
    });
    
    // Clone the page without caption elements
    const ClonedPageId: any = generateUniqueId()
    originalPage.clone({ id: ClonedPageId })
    const clonedPage = store.getElementById(ClonedPageId)
    store.selectPage(ClonedPageId);
    
    // Remove videos and images from cloned page
    clonedPage.children.map((child: any) => {
      if (child.type == 'video' || child.type == 'image') {
        store.deleteElements([child.id])
      }
    })
    
    // Add new media element
    const elementId = generateUniqueId()
    clonedPage.addElement({
      type: item.type,
      src: item.video_url || item.image_url,
      x: -25,
      y: -25,
      width: store.width + 50,
      height: store.height + 50,
      id: elementId,
      // mask:true,
      custom: {
        gettyId: item.id,
        voiceOver: scriptDetails?.data?.[selectedSectionIndex].voiceOver,
        aiAvatar: false,
      },
    })

    // Add caption elements back to both pages
    captionElements.forEach((element) => {
      // Add back to original page with original ID
      originalPage.addElement({
        ...element
      });
      
      // Add to cloned page with new ID
      const newCaptionId = 'captionText'+ClonedPageId;
      clonedPage.addElement({
        ...element,
        id: newCaptionId
      });
    });

    let isAvatar = false
    originalPage.children.map((child: any) => {
      if (child.type == 'video' && child?.custom?.aiAvatar) {
        store.deleteElements([pageId])
        isAvatar = true
      }
    })
 
    durationCalculation()
    setScriptDetails((prevScript: { data: any }) => {
      const updatedScript = { ...prevScript }

      const updatedData = [...prevScript.data]
      const pages = store.toJSON().pages
      const totalPages = pages
        .filter((page: any) => page?.custom?.scriptIndex === selectedSectionIndex)
        .map((page: any) => page.id)
      const images = []
      for (let i = 0; i < totalPages.length; i++) {
        images.push({ searchQueries: [''], duration: 0.9 })
      }
      updatedData[selectedSectionIndex] = {
        ...updatedData[selectedSectionIndex],
        images,
      }
      return { ...updatedScript, data: updatedData }
    })
 
  }

  const addPageSceneWithBackUpImage = (backupImage: any) => {
    // Find the page ID for the selected section index
    const pageId = store.pages.find((page: any) => page?.custom?.scriptIndex === selectedSectionIndex)?.id;

    if (!pageId) return;
    let previousPageId = pageId

    // Process each backup image
    backupImage.forEach((image: any) => {
      // Get the original page
      const originalPage = store.getElementById(previousPageId);
      
      // Find and temporarily remove caption elements
      const captionElements: any[] = [];
      originalPage.children.forEach((child: any) => {
        if (child.id.includes('captionText')) {
          captionElements.push({
            ...child.toJSON(),
            id: child.id
          });
          store.deleteElements([child.id]);
        }
      });
      
      // Generate a unique ID for the cloned page
      const clonedPageId = generateUniqueId();
      
      // Clone the original page
      originalPage.clone({ id: clonedPageId });
      const clonedPage = store.getElementById(clonedPageId);
      store.selectPage(clonedPageId);
      
      // Remove all video and image elements from the cloned page
      const elementsToDelete = clonedPage.children
        .filter((child: any) => child.type === 'video' || child.type === 'image')
        .map((child: any) => child.id);
      
      if (elementsToDelete.length > 0) {
        store.deleteElements(elementsToDelete);
      }
   
      // Add the new element
      const elementId = generateUniqueId();
      clonedPage.addElement({
        src: image.mediaUrl,
        type: image.type,
        id: elementId,
        x: -25,
        y: -25,
        width: store.width + 50,
        height: store.height + 50,
        custom: {
          voiceOver: scriptDetails?.data?.[selectedSectionIndex].voiceOver,
          aiAvatar: false,
        },
      });
      
      // Add caption elements back to both pages
      captionElements.forEach((element) => {
        // Add back to original page with original ID
        originalPage.addElement({
          ...element
        });
        
        // Add to cloned page with new ID
        const newCaptionId = 'captionText'+clonedPageId;
        clonedPage.addElement({
          ...element,
          id: newCaptionId
        });
      });
      
      // Check if the original page has an AI avatar
      const hasAvatar = originalPage.children.some(
        (child: any) => child.type === 'video' && child?.custom?.aiAvatar
      );
      
      if (hasAvatar) {
        store.deleteElements([previousPageId]);
      }
      previousPageId = clonedPageId

    });
    
    // Update duration calculation
    durationCalculation();
    
    // Update script details
    setScriptDetails((prevScript: { data: any }) => {
      const updatedData = [...prevScript.data];
      
      // Get all pages for the selected section
      const sectionPages = store.toJSON().pages
        .filter((page: any) => page?.custom?.scriptIndex === selectedSectionIndex);
      
      // Create image entries for each page
      const images = sectionPages.map(() => ({ searchQueries: [''], duration: 0.9 }));
      
      // Update the selected section with the new images
      updatedData[selectedSectionIndex] = {
        ...updatedData[selectedSectionIndex],
        images,
      };
      
      return { ...prevScript, data: updatedData };
    });
  };

  const handleBackGroundAvatar = async (item: any, snapshotImageUrl?: string) => {
    if (snapshotImageUrl === ''){
      message.error('No snapshot image URL available. Please generate a new snapshot.');
      return;
    }
    const backupAssets: any = []
    const pages = store.toJSON().pages
    const currentIndexPages = pages
      .filter((page: any) => page?.custom?.scriptIndex === selectedSectionIndex)
      .map((page: any) => ({
        id: page.id,
        position: pages.findIndex((p: any) => p.id === page.id),
      }))
      .sort((a: any, b: any) => a.position - b.position)
      .map((page: any) => page?.id)
    
    // Only delete pages if there are multiple pages
    if (currentIndexPages.length > 1) {
      // Get all page IDs except the last one
      const pageIdsToDelete = currentIndexPages.slice(0, -1)
      
      // Calculate total duration from pages to be deleted
      let totalDeletedDuration = 0
      currentIndexPages.forEach((pageId: string, index: number) => {
        const page = store.getElementById(pageId)
        // Find the first video or image child and break
        for (const child of page.children) {
          if (child.type === 'video' || child.type === 'image') {
            backupAssets.push({searchQueries:[''], duration:0.9, mediaUrl:child?.src, type:child?.type})
            break; // Stop after finding the first video or image
          }
        }
        if (page && page.duration && index !== currentIndexPages.length - 1) {
          totalDeletedDuration += page.duration
        }
      })
      
      // Get the last page that will remain
      const lastPageId = currentIndexPages[currentIndexPages.length - 1]
      const lastPage = store.getElementById(lastPageId)
      const currentDuration = lastPage.duration || 0
      
      // Delete all pages except the last one
      store.deletePages(pageIdsToDelete)
      
      // Add the collected duration to the remaining page
      lastPage.set({
        duration: currentDuration + totalDeletedDuration
      })
    }
    
    const lastPageId = currentIndexPages[currentIndexPages.length - 1]
    const selectedpage = store.getElementById(lastPageId)
    store.selectPage(lastPageId);
    const prevChildSrc: any = []
    selectedpage.children.map((child: any) => {
      if (child.type == 'video' || child.type == 'image') {
        const obj = { src: child.src, type: child.type }
        prevChildSrc.push(obj)
        store.deleteElements([child.id])
      }
    })
    // Use the state instead of making the API call
    const initialAvatarUrl = replicasTavus.find((replica: any) => {
      const avatarReplicaId = userDetail?.purpose?.avatarReplicaId
      const sizeCondition = userDetail?.purpose?.size === '9:16'
      let replicaId = ''
      if (avatarReplicaId) {
        replicaId = avatarReplicaId
      } else {
        replicaId = sizeCondition ? 'ra5e60eeb07f' : 'r79e1c033f'
      }
      return replica.replica_id === replicaId
    })

    const videoChildId = generateUniqueId()
    if (item) {
      selectedpage.addElement({
        src: item.video_url || item.image_url,
        type: item.type,
        id: videoChildId,
        x: -25,
        y: -25,
        width: store.width + 50,
        height: store.height + 50,
        custom: {
          gettyId: videoChildId,
          mainElement: false,
          aiAvatar: false,
        },
      })
    } else if (snapshotImageUrl) {
      selectedpage.addElement({
        src: snapshotImageUrl,
        type: 'image',
        id: videoChildId,
        x: -25,
        y: -25,
        width: store.width + 50,
        height: store.height + 50,
        custom: {
          gettyId: videoChildId,
          mainElement: false,
          aiAvatar: false,
        },
      })
    }

    const videoIdMain = generateUniqueId()
    selectedpage.addElement({
      src: initialAvatarUrl?.thumbnail_video_url || '',
      type: 'video',
      id: videoIdMain,
      x: -1,
      y: store.height - store.height / 3,
      width: store.width / 3,
      height: store.height / 3,
      cornerRadius: 20,
      borderRadius: 10,
      custom: {
        gettyId: videoIdMain,
        mainElement: true,
        aiAvatar: true,
        bg: true,
      },
    })
    // if(snapshotImageUrl){
      setScriptDetails((prevScript: { data: any }) => {
        const updatedScript = { ...prevScript }
        const updatedData = [...prevScript.data]
        updatedData[selectedSectionIndex] = {
          ...updatedData[selectedSectionIndex],
          aiAvatarProps: {
            ...updatedData[selectedSectionIndex].aiAvatarProps,
            noAvatar: false,
            avatarWithBg: snapshotImageUrl ? false : true,
            avatar: false,
            avatarWithWebsite: snapshotImageUrl ? true : false,
          },
          images: [updatedData[selectedSectionIndex]?.images?.[0] || {searchQueries: [], duration: 0.9, mediaUrl: item.video_url || item.image_url || '', type: item?.type || 'image'}],
          backupImages: backupAssets ?? [],

        }
      
        return { ...updatedScript, data: updatedData }
      })
    // }
    
  }

  const [isSnapshotModalVisible, setIsSnapshotModalVisible] = useState(false);
  const [snapshotUrl, setSnapshotUrl] = useState('');
  const [snapshotOrientation, setSnapshotOrientation] = useState('horizontal');
  const [isGeneratingSnapshot, setIsGeneratingSnapshot] = useState(false);
  const [taskId, setTaskId] = useState('');
  const [urlError, setUrlError] = useState('');

  const validateUrl = (url: string) => {
    try {
      new URL(url);
      setUrlError('');
      return true;
    } catch (e) {
      setUrlError('Please enter a valid URL (e.g., https://www.example.com)');
      return false;
    }
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSnapshotUrl(value);
    if (value) {
      validateUrl(value);
    } else {
      setUrlError('');
    }
  };

  const handleAvatarWithWebsite = async () => {
    if (userDetail?.purpose?.snapShotUrl) {
      Modal.confirm({
        title: (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            width: '100%'
          }}>
            <span>Use Existing Snapshot?</span>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => {
                Modal.destroyAll();
              }}
              style={{
                color: 'var(--color-gray-600)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '4px',
                borderRadius: '8px',
                transition: 'all 0.2s ease'
              }}
            />
          </div>
        ),
        content: 'Would you like to use the existing website snapshot or take a new one?',
        okText: 'Use Existing',
        cancelText: 'Take New',
        onOk: () => {
          handleBackGroundAvatar(null, userDetail.purpose.snapShotUrl);
        },
        onCancel: () => {
          setIsSnapshotModalVisible(true);
        }
      });
    } else {
      setIsSnapshotModalVisible(true);
    }
  };

  const handleGenerateSnapshot = async () => {
    if (!snapshotUrl) {
      message.error('Please enter a URL');
      return;
    }

    if (!validateUrl(snapshotUrl)) {
      return;
    }

    setIsGeneratingSnapshot(true);
    try {
      const response = await generateSnapShot(snapshotUrl, snapshotOrientation);
      if (response?.task_id) {
        setTaskId(response.task_id);
        checkSnapshotStatus(response.task_id);
      }
    } catch (error) {
      message.error('Failed to generate snapshot');
      setIsGeneratingSnapshot(false);
    }
  };

  const checkSnapshotStatus = async (taskId: string) => {
    const checkStatus = async () => {
      try {
        const response = await checkVideoGeneratedStatus(taskId);
        if (response?.status === 'Done') {
          handleBackGroundAvatar(null, response.data.image_url);
          setIsSnapshotModalVisible(false);
          setIsGeneratingSnapshot(false);
        } else if (response?.status === 'Error') {
          message.error('Snapshot generation failed');
          setIsGeneratingSnapshot(false);
        } else {
          setTimeout(checkStatus, 2000); // Check again after 2 seconds
        }
      } catch (error) {
        message.error('Failed to check snapshot status');
        setIsGeneratingSnapshot(false);
      }
    };

    checkStatus();
  };

  return (
    <div style={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      background: 'var(--color-gray-50)'
    }}>
      {/* Header */}
      <div style={{ 
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 24px',
        borderBottom: '1px solid var(--color-gray-200)',
        background: 'white'
      }}>
        <h2 style={{ 
          margin: 0,
          fontSize: '18px',
          fontWeight: 600,
          color: 'var(--color-gray-900)'
        }}>
          Presentation Settings
        </h2>
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={() => setShowPresentation(false)}
          style={{
            color: 'var(--color-gray-600)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '8px',
            borderRadius: '8px',
            transition: 'all 0.2s ease'
          }}
        />
      </div>
      
      {/* Content */}
      <div style={{ 
        flex: 1,
        overflowY: 'auto',
        padding: '16px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
        alignContent: 'flex-start',
        justifyContent: 'center'
      }}>
        {/* Avatar Option */}
        <div 
          style={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center',
            width: '140px', 
            height: '140px', 
            backgroundColor: 'white', 
            borderRadius: '8px', 
            border: tabPositionAvatar === 'avatar' ? '2px solid var(--color-primary-500)' : '1px solid var(--color-gray-200)',
            overflow: 'hidden',
            cursor: 'pointer',
            opacity: tabPositionAvatar === 'avatar' ? 1 : 0.7,
            transition: 'all 0.2s ease',
            boxShadow: tabPositionAvatar === 'avatar' ? '0 4px 12px rgba(0,0,0,0.1)' : '0 2px 8px rgba(0,0,0,0.05)',
            position: 'relative'
          }} 
          className='avatar presentation-card'
          onClick={() => fullAvatarScene()}
        >
          <div style={{
            width: '100%',
            height: 'calc(100% - 32px)', // Reduced from 40px
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '12px' // Reduced from 20px
          }}>
            <img 
              src={'/images/fullactor.jpg'} 
              alt="Full Avatar" 
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block'
              }} 
            />
          </div>
          <div style={{
            width: '100%',
            padding: '6px', // Reduced from 8px
            background: 'rgba(255,255,255,0.9)',
            textAlign: 'center',
            fontSize: '12px', // Reduced from 14px
            fontWeight: 500,
            color: 'var(--color-gray-900)',
            borderTop: '1px solid var(--color-gray-200)'
          }}>
            Full Avatar
          </div>
        </div>

        {/* Avatar with Background Option */}
        <div 
          style={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center',
            width: '140px', 
            height: '140px', 
            backgroundColor: 'white', 
            borderRadius: '8px', 
            border: tabPositionAvatar === 'avatarWithBg' ? '2px solid var(--color-primary-500)' : '1px solid var(--color-gray-200)',
            overflow: 'hidden',
            cursor: 'pointer',
            opacity: tabPositionAvatar === 'avatarWithBg' ? 1 : 0.7,
            transition: 'all 0.2s ease',
            boxShadow: tabPositionAvatar === 'avatarWithBg' ? '0 4px 12px rgba(0,0,0,0.1)' : '0 2px 8px rgba(0,0,0,0.05)',
            position: 'relative'
          }} 
          className='avatarWithBg presentation-card'
          onClick={() => handleMediaBroll('avatarWithBg')}
        >
          <div style={{
            width: '100%',
            height: 'calc(100% - 32px)', // Reduced from 40px
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'flex-start',
            padding: '12px' // Reduced from 20px
          }}>
            <img 
              src={'/images/withmedia.jpg'} 
              alt="Avatar with Background" 
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block'
              }} 
            />
          </div>
          <div style={{
            width: '100%',
            padding: '6px', // Reduced from 8px
            background: 'rgba(255,255,255,0.9)',
            textAlign: 'center',
            fontSize: '12px', // Reduced from 14px
            fontWeight: 500,
            color: 'var(--color-gray-900)',
            borderTop: '1px solid var(--color-gray-200)'
          }}>
            Avatar with Background
          </div>
        </div>
        
        {/* Avatar with Website Option */}
        <div 
          style={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center',
            width: '140px', 
            height: '140px', 
            backgroundColor: 'white', 
            borderRadius: '8px', 
            border: tabPositionAvatar === 'avatarWithWebsite' ? '2px solid var(--color-primary-500)' : '1px solid var(--color-gray-200)',
            overflow: 'hidden',
            cursor: 'pointer',
            opacity: tabPositionAvatar === 'avatarWithWebsite' ? 1 : 0.7,
            transition: 'all 0.2s ease',
            boxShadow: tabPositionAvatar === 'avatarWithWebsite' ? '0 4px 12px rgba(0,0,0,0.1)' : '0 2px 8px rgba(0,0,0,0.05)',
            position: 'relative'
          }} 
          className='avatarWithWebsite presentation-card'
          onClick={() => handleAvatarWithWebsite()}
        >
          <div style={{
            width: '100%',
            height: 'calc(100% - 32px)', // Reduced from 40px
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'flex-start',
            padding: '12px' // Reduced from 20px
          }}>
            <img 
              src={'/images/withwebsite.jpg'} 
              alt="Avatar with Website" 
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block'
              }} 
            />
          </div>
          <div style={{
            width: '100%',
            padding: '6px', // Reduced from 8px
            background: 'rgba(255,255,255,0.9)',
            textAlign: 'center',
            fontSize: '12px', // Reduced from 14px
            fontWeight: 500,
            color: 'var(--color-gray-900)',
            borderTop: '1px solid var(--color-gray-200)'
          }}>
            Avatar with Website
          </div>
        </div>
        
        {/* No Avatar Option */}
        <div 
          style={{
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center',
            width: '140px', 
            height: '140px', 
            backgroundColor: 'white', 
            borderRadius: '8px', 
            border: tabPositionAvatar === 'noAvatar' ? '2px solid var(--color-primary-500)' : '1px solid var(--color-gray-200)',
            overflow: 'hidden',
            cursor: 'pointer',
            opacity: tabPositionAvatar === 'noAvatar' ? 1 : 0.7,
            transition: 'all 0.2s ease',
            boxShadow: tabPositionAvatar === 'noAvatar' ? '0 4px 12px rgba(0,0,0,0.1)' : '0 2px 8px rgba(0,0,0,0.05)',
            position: 'relative'
          }} 
          className='noAvatar presentation-card'
          onClick={() => handleMediaBroll('noAvatar')}
        >
          <div style={{
            width: '100%',
            height: 'calc(100% - 32px)', // Reduced from 40px
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '12px' // Reduced from 20px
          }}>
            <img 
              src={'/images/broll.jpg'} 
              alt="No Avatar" 
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block'
              }} 
            />
          </div>
          <div style={{
            width: '100%',
            padding: '6px', // Reduced from 8px
            background: 'rgba(255,255,255,0.9)',
            textAlign: 'center',
            fontSize: '12px', // Reduced from 14px
            fontWeight: 500,
            color: 'var(--color-gray-900)',
            borderTop: '1px solid var(--color-gray-200)'
          }}>
            No Avatar
          </div>
        </div>

       
      </div>

      {/* Snapshot Modal */}
      <Modal
        title="Take Website Snapshot"
        open={isSnapshotModalVisible}
        onCancel={() => setIsSnapshotModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsSnapshotModalVisible(false)}>
            Cancel
          </Button>,
          <Button 
            key="generate" 
            type="primary" 
            onClick={handleGenerateSnapshot}
            loading={isGeneratingSnapshot}
          >
            Generate Snapshot
          </Button>
        ]}
      >
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px' }}>Website URL</label>
          <Input
            value={snapshotUrl}
            onChange={handleUrlChange}
            placeholder="Enter website URL (e.g., https://www.example.com)"
            status={urlError ? 'error' : ''}
          />
          {urlError && (
            <div style={{ 
              color: '#ff4d4f', 
              fontSize: '12px', 
              marginTop: '4px' 
            }}>
              {urlError}
            </div>
          )}
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '8px' }}>Orientation</label>
          <Select
            value={snapshotOrientation}
            onChange={setSnapshotOrientation}
            style={{ width: '100%' }}
          >
            <Select.Option value="horizontal">Horizontal</Select.Option>
            <Select.Option value="vertical">Vertical</Select.Option>
          </Select>
        </div>
      </Modal>
    </div>
  );
});